import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import gotchiIcon from '@assets/images/characters/gotchi.png'
import './GotchiDashCelebration.scss'

const TOTAL_MS = 3000

type Role = 'blitz' | 'returner' | 'pusher' | 'early' | 'slow'

type Runner = {
  id: number
  role: Role
  topPct: number
  sizePx: number
  durationMs: number
  delayMs: number
  waddlePx: number
  rotateDeg: number
  zIndex: number
}

type Props = {
  active: boolean
  onDone?: () => void
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** 6 gotchis: 2 blitz, 1 returner, 1 pusher (behind slow), 1 early exit, 1 slow. */
function buildRunners(): Runner[] {
  const laneSlow = 46 + Math.random() * 8
  const laneHigh = 18 + Math.random() * 10
  const laneLow = 68 + Math.random() * 10
  const laneMid = 32 + Math.random() * 8

  return [
    {
      id: 0,
      role: 'blitz',
      topPct: laneHigh,
      sizePx: 30 + Math.floor(Math.random() * 8),
      durationMs: 520 + Math.floor(Math.random() * 80),
      delayMs: 20,
      waddlePx: 6,
      rotateDeg: 14,
      zIndex: 1,
    },
    {
      id: 1,
      role: 'blitz',
      topPct: laneLow,
      sizePx: 28 + Math.floor(Math.random() * 10),
      durationMs: 480 + Math.floor(Math.random() * 70),
      delayMs: 60,
      waddlePx: 7,
      rotateDeg: 16,
      zIndex: 1,
    },
    {
      id: 2,
      role: 'returner',
      topPct: laneMid,
      sizePx: 40,
      durationMs: 2100,
      delayMs: 40,
      waddlePx: 12,
      rotateDeg: 11,
      zIndex: 2,
    },
    {
      id: 3,
      role: 'pusher',
      topPct: laneSlow + 0.4,
      sizePx: 44,
      durationMs: 2800,
      delayMs: 80,
      waddlePx: 10,
      rotateDeg: 12,
      zIndex: 4,
    },
    {
      id: 4,
      role: 'early',
      topPct: 58 + Math.random() * 8,
      sizePx: 36,
      durationMs: 1100,
      delayMs: 30,
      waddlePx: 10,
      rotateDeg: 10,
      zIndex: 2,
    },
    {
      id: 5,
      role: 'slow',
      topPct: laneSlow,
      sizePx: 54,
      durationMs: TOTAL_MS,
      delayMs: 50,
      waddlePx: 14,
      rotateDeg: 12,
      zIndex: 3,
    },
  ]
}

/**
 * Dash L→R (~3s): 2 blitz one-way, 1 returner, 1 pusher behind the slowpoke,
 * 1 early exit (no return), 1 slow.
 */
export function GotchiDashCelebration({ active, onDone }: Props) {
  const [visible, setVisible] = useState(false)
  const [runners, setRunners] = useState<Runner[]>([])

  useEffect(() => {
    if (!active) {
      setVisible(false)
      return
    }
    if (prefersReducedMotion()) {
      onDone?.()
      return
    }
    setRunners(buildRunners())
    setVisible(true)
    const t = window.setTimeout(() => {
      setVisible(false)
      onDone?.()
    }, TOTAL_MS)
    return () => window.clearTimeout(t)
  }, [active, onDone])

  const root = useMemo(() => {
    if (typeof document === 'undefined') return null
    return document.querySelector('.AppLayout') ?? document.body
  }, [visible])

  if (!visible || !root) return null

  return createPortal(
    <div className="GotchiDashCelebration" aria-hidden>
      {runners.map((r) => (
        <img
          key={r.id}
          src={gotchiIcon}
          alt=""
          className={`GotchiDashCelebration-gotchi GotchiDashCelebration-gotchi--${r.role}`}
          style={
            {
              top: `${r.topPct}%`,
              width: r.sizePx,
              height: r.sizePx,
              zIndex: r.zIndex,
              '--gd-duration': `${r.durationMs}ms`,
              '--gd-delay': `${r.delayMs}ms`,
              '--gd-waddle': `${r.waddlePx}px`,
              '--gd-rotate': `${r.rotateDeg}deg`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>,
    root,
  )
}

export default GotchiDashCelebration
